/**
 * Localization switcher
 *
 * 1 . Import the Fancybox library and show the pop-up content when the localization switcher trigger is clicked.
 *
 * 2 . Handle the country/language selection and url setup
 * The basic url setup is set on Liquid, but we also have to look for the counterpart of the same current link in another store
 * E.g. When we use the localization switcher on another page, other than the homepage, for example a PDP or PLP,
 * the submit button should be redirected to that counterpart of the linked store.
 * We use the rendered alternate links from the Multi Store Hreflang Tags app to find the right counter part.
 *
 */

(async () => {

  /**
   * Handles the click on the Localization switcher trigger and shows the Fancybox popup for Localization selection
   */

  // import Fancybox
  const { Fancybox } = await import("@fancyapps/ui");

  // get the localization switcher triggers and pop-up content
  const localizationSwitcherTriggers = document.querySelectorAll('[data-fancybox="localization-switcher"]');
  const popupContent = document.querySelector('[data-localization-switcher-pop-up-content]').innerHTML;

  // listen for click on any localization switcher trigger and show the pop-up content
  localizationSwitcherTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      Fancybox.show([{ src: popupContent, type: "html", template: popupContent }], {
        mainClass: 'localization-switcher__popup'
      });
    });
  });

})();

class LocalizationSwitcherDropdowns extends HTMLElement {
  connectedCallback() {
    const localizationSwitcherOptions = this.querySelectorAll('li');
    const localizationSwitcherWrapper = this.closest('[data-localization-dropdowns-wrapper]');
    this.countriesCollapsible = this.querySelector('[data-localization-type="country"]');
    this.languagesCollapsible = this.querySelector('[data-localization-type="language"]');
    this.selectedCountryUrl = this.querySelector('[data-current-country-url]').dataset.currentCountryUrl;
    this.selectedLanguageUrl = this.querySelector('[data-current-language-url]').dataset.currentLanguageUrl || '';
    this.confirmButton = localizationSwitcherWrapper.querySelector('[data-localization-switcher-link]');

    // update the confirm/submit button url on load, in case there's a counterpart url in the hreflang tags
    this.updateConfirmButtonUrl();

    // listen for click on each dropdown option
    localizationSwitcherOptions.forEach(option => {
      option.addEventListener('click', (event) => this.handleOptionClick(event));
    });
  }


  /**
   * handleOptionClick
   * @description Handles the click event on each dropdown option (country or language)
   */
  handleOptionClick(event) {
    const item = event.target;
    const collapsible = item.closest('collapsible-element');

    this.updateCurrentSelection(item, collapsible);

    // user changed language/country via dropdown: set cookie so country redirect does not redirect
    this.setLanguagePickedCookie();

    // update the confirm/submit button url
    this.updateConfirmButtonUrl();

    // close collapsible after selection
    collapsible.toggle(collapsible.querySelector('[data-collapsible-group]'));
  }

  /**
   * setLanguagePickedCookie
   * @description Set cookie when user changes the localization dropdown so country redirect does not redirect on future visits
   */
  setLanguagePickedCookie() {
    const countryRedirect = document.querySelector('country-redirect');
    const days = countryRedirect?.dataset?.expires != null ? parseInt(countryRedirect.dataset.expires, 10) : 365;
    const maxAge = typeof days === 'number' && days > 0 ? days * 60 * 60 * 24 : 365 * 60 * 60 * 24;
    document.cookie = `country_redirect_language_picked=true; path=/; max-age=${maxAge};`;
  }


  /**
   * updateCurrentSelection
   * @description Updates the selected country/language in the dropdown(s) trigger(s)
   * @param {HTMLElement} item - The clicked dropdown option element
   * @param {HTMLElement} collapsible - The parent collapsible element containing the dropdown
   */
  updateCurrentSelection(item, collapsible) {
    const triggerCurrentName = collapsible.querySelector('[data-current-name]');
    const triggerCurrentCountryFlag = collapsible.querySelector('[data-current-flag]');
    const localizationType = collapsible.dataset.localizationType;

    // Update the current language/country name + country flag in the collapsible trigger & update the available languages
    if (triggerCurrentName) triggerCurrentName.textContent = item.textContent;

    // show all languages/countries and set the selected one active
    collapsible.querySelectorAll('li').forEach(li => li.classList.remove('active', 'unavailable'));
    item.classList.add('active');

    if (localizationType == 'language') {
      const firstEnabledLanguage = this.languagesCollapsible?.querySelector('li:not(.unavailable)')?.dataset.value;
      // update the selected language url to blank when the selected language is the first enabled language
      this.selectedLanguageUrl = item.dataset.value === firstEnabledLanguage ? '' : item.dataset.value;
    }
    else if (localizationType == 'country') {
      this.selectedCountryUrl = item.dataset.value;

      const newFlag = item.querySelector('[data-country-flag]');
      triggerCurrentCountryFlag?.classList.toggle('hidden', !newFlag);
      if (triggerCurrentCountryFlag && newFlag) {
        triggerCurrentCountryFlag.innerHTML = newFlag.innerHTML;
      }

      this.updateAvailableLanguages(item);
    }

    // update the collapsible state based on the nr of available countries and languages
    this.updateCollapsibleState();
  }


  /**
   * updateAvailableLanguages
   * @description Updates the available languages in the localization switcher based on the selected country
   * @param {HTMLElement} item - The clicked country dropdown option element
   */
  updateAvailableLanguages(item) {
    // get the available language codes from the country item
    const countryAvailableLanguages = item.dataset.countryLanguages;

    if (!countryAvailableLanguages && !this.languagesCollapsible) return;

    // convert comma-separated string to array and trim whitespace
    const languagesArray = countryAvailableLanguages.split(',').map(lang => lang.trim().toLowerCase());
    const languageDropdownOptions = this.languagesCollapsible.querySelectorAll('li');

    // get the dropdown list language options - set classes according to being 'unavailable' or 'active'
    languageDropdownOptions.forEach(option => {
      const value = option.dataset.value;
      const currentLanguage = this.languagesCollapsible.querySelector('[data-current-name]').textContent;
      option.classList.toggle('unavailable', !languagesArray.includes(value));
      option.classList.toggle('active', option.textContent.trim() === currentLanguage.trim());
    });

    // update the selected language to the next available one, if it's not available in the new country
    const triggerLanguageElem = this.languagesCollapsible.querySelector('[data-current-name]');
    const selectedLanguageCode = Array.from(languageDropdownOptions)
      .find(li => li.textContent.trim() === triggerLanguageElem.textContent.trim())?.dataset.value;

    const firstLanguageOptionAvailable = this.languagesCollapsible.querySelector(`li[data-value="${languagesArray[0]}"]`);

    // return the selectedLanguageUrl if the selected language is available. Return empty if it's the 1st language
    if (languagesArray.includes(selectedLanguageCode)) {
      this.selectedLanguageUrl = selectedLanguageCode === firstLanguageOptionAvailable.dataset.value ? '' : this.selectedLanguageUrl = selectedLanguageCode;
      return;
    }

    triggerLanguageElem.textContent = firstLanguageOptionAvailable.textContent;

    languageDropdownOptions.forEach(option => {
      option.classList.remove('active');
    });
    firstLanguageOptionAvailable.classList.add('active');
    this.selectedLanguageUrl = '';

  }


  /**
   * updateCollapsibleState
   * @description Sets class to enable/disable the collapsible based on the nr of available countries and languages
   */
  updateCollapsibleState() {
    const visibleCountries = this.countriesCollapsible.querySelectorAll('li:not(.active):not(.unavailable)');
    const visibleLanguages = this.languagesCollapsible.querySelectorAll('li:not(.active):not(.unavailable)');

    this.countriesCollapsible.querySelector('[data-collapsible-group]').classList.toggle('disable', visibleCountries.length < 1);
    this.languagesCollapsible.querySelector('[data-collapsible-group]').classList.toggle('disable', visibleLanguages.length < 1);
  }


  /**
   * updateConfirmButtonUrl
   * @description Updates the URL of the confirm button based on the selected country/language and the counterpart url
   */
  updateConfirmButtonUrl() {
    // get current country and language url
    const selectedUrl = `${this.selectedCountryUrl}/${this.selectedLanguageUrl}`;

    // check if we have a counterPartUrl otherwise fallback to the original href (=homepage)
    let urlToOpen = selectedUrl || this.confirmButton.href;
    const newUrl = urlToOpen ? this.getCounterPartUrl(this.removeProtocol(urlToOpen)) : false;
    if (newUrl) urlToOpen = newUrl;

    // add current url attributes
    urlToOpen = urlToOpen + location.search;

    // update confirm link
    this.confirmButton.href = urlToOpen;
  }


  /**
   * removeProtocol
   * @description Remove http or https from url
   */
  removeProtocol(url) {
    const domain = (new URL(url));
    if (!domain.host) return false;

    const firstUrlPath = domain.pathname.split('/')[1];

    // return the url with the language code if there's a language (2 digits) in the url, otherwise return the domain host only
    return firstUrlPath.length == 2 ? `${domain.host}/${firstUrlPath}` : domain.host;
  }


  /**
   * getCounterPartUrl
   * @description Find the right counter part by using the alternate links that the app Multi Store Hreflang renders.
   * @param {string} url - The url to find the counter part for
   * @returns {string} The counter part url
   */
  getCounterPartUrl(url) {
    // Find the store to link to. Store item contains the primary domain and myshopify domain.
    // The reason why we need to find the domain and shopify domain is because  Multi Store Hreflang sometimes outputs the primary domain and sometimes the myshopify domain.
    const stores = this.dataset.stores;
    const storesArray = stores?.split(',');
    if (storesArray.length === 0) return false;

    let storeToLinkTo = null;
    storesArray.forEach(function(store){
      let storeArr = store.split('|');

      const storeUrlMatch = storeArr.find(storeUrl => storeUrl == url);
      if (storeUrlMatch) storeToLinkTo = storeUrlMatch;

    });

    // get counterPartLink
    const alternateLinksArray = [...document.querySelectorAll('link[rel="alternate"]')];
    if (alternateLinksArray.length === 0) return false;

    // get matching link and return value
    const alternateLinkHrefs = alternateLinksArray
      .filter(link => link.hasAttribute('hreflang'))
      .map(link => link.href);
    const counterPartLink = alternateLinkHrefs?.find(href => href.indexOf(`https://${storeToLinkTo}`) > -1) || null;

    return counterPartLink || false;
  }
}

customElements.define('localization-switcher-dropdowns', LocalizationSwitcherDropdowns);
